/**
 * Leer y guardar el informe de una visita SIEMPRE contra la fila de la base.
 *
 * El fallo C3 (T07-01) nació de guardar lo que había en pantalla: el
 * planificador del admin no pedía `visit_report` ni `photos`, la ficha decía «Sin
 * informe todavía» y «Editar informe» guardaba un formulario vacío encima del
 * informe del perito. Aquí:
 *  · la ficha y el formulario leen la fila ELLOS (no dependen de qué columnas
 *    pidió la pantalla que los abre: planificador, tareas, equipo, avisos…);
 *  · guardar vuelve a leer la fila y FUSIONA (`mergeVisitReport`): lo que no se
 *    toca se conserva, también el comentario de la empresa;
 *  · el comentario de la empresa va aparte y nunca toca el informe.
 * La base lo refuerza con el disparador `guard_visit_report` (migración
 * 20260926140000): el informe solo lo cambia quien tiene la visita asignada.
 */
import { supabase } from "@/integrations/supabase/client";
import { mergeVisitReport, type VisitReport, type VisitReportPatch } from "./visitReport";

export interface VisitReportRow {
  id: string;
  company_id: string | null;
  type: string | null;
  status: string | null;
  title?: string | null;
  assigned_to: string | null;
  created_by: string | null;
  completed_at: string | null;
  visit_report: VisitReport | null;
  photos: string[] | null;
  /** user_id → nombre, para «Realizada por», «Editado por» y el comentario. */
  names: Record<string, string>;
}

const ROW_SELECT =
  "id, company_id, type, status, title, assigned_to, created_by, completed_at, visit_report, photos";

export const visitReportQueryKey = (taskId: string | null | undefined) => ["visit-report", taskId ?? null] as const;

/** La fila tal y como está en la base, con los nombres de quien aparece en ella. Lanza si no se puede leer. */
export async function fetchVisitReport(taskId: string): Promise<VisitReportRow> {
  const { data, error } = await supabase
    .from("planner_tasks")
    .select(ROW_SELECT)
    .eq("id", taskId)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("VISIT_NOT_FOUND");
  const row = data as unknown as Omit<VisitReportRow, "names">;
  const report = (row.visit_report ?? null) as VisitReport | null;
  const ids = [
    row.assigned_to,
    report?.completed_by,
    report?.edited_by,
    report?.company_comment?.by,
  ].filter((v): v is string => typeof v === "string" && v.length > 0);
  const names: Record<string, string> = {};
  if (ids.length > 0) {
    // Si falla, la ficha se enseña igual sin nombres: perder el informe por no saber un nombre sería peor.
    const { data: profs } = await supabase
      .from("profiles")
      .select("user_id, full_name")
      .in("user_id", [...new Set(ids)]);
    for (const p of (profs ?? []) as Array<{ user_id: string; full_name: string | null }>) {
      if (p.full_name) names[p.user_id] = p.full_name;
    }
  }
  return { ...row, visit_report: report, names };
}

/**
 * Guarda una EDICIÓN del informe (no completa la visita). Relee la fila, fusiona y
 * escribe informe y fotos. Si la fila no se puede leer, no guarda nada.
 */
export async function saveVisitReportEdit(
  taskId: string,
  patch: VisitReportPatch,
  photos: string[],
): Promise<void> {
  const fresh = await fetchVisitReport(taskId);
  const merged = mergeVisitReport(fresh.visit_report, { ...patch, photos });
  const { data, error } = await supabase
    .from("planner_tasks")
    .update({ visit_report: merged as never, photos })
    .eq("id", taskId)
    .select("id");
  if (error) throw error;
  // La RLS no da error cuando no deja: simplemente no toca ninguna fila.
  if (!data || data.length === 0) throw new Error("NOT_ALLOWED");
}

/** El comentario de la empresa sobre el informe. Vacío = se quita. No toca el informe. */
export async function saveVisitCompanyComment(taskId: string, text: string, userId: string | null | undefined): Promise<void> {
  const fresh = await fetchVisitReport(taskId);
  const base = { ...(fresh.visit_report ?? {}) } as VisitReport;
  const clean = text.trim();
  if (clean) base.company_comment = { text: clean, by: userId ?? null, at: new Date().toISOString() };
  else delete base.company_comment;
  const { data, error } = await supabase
    .from("planner_tasks")
    .update({ visit_report: base as never })
    .eq("id", taskId)
    .select("id");
  if (error) throw error;
  if (!data || data.length === 0) throw new Error("NOT_ALLOWED");
}

/**
 * El código de error que devuelve `update-task-status` (403 NOT_ALLOWED /
 * REPORT_AUTHOR_ONLY). `functions.invoke` no expone el cuerpo del error (E8):
 * hay que leerlo del `context` de la respuesta.
 */
export async function edgeErrorCode(response: { data?: unknown; error?: unknown }): Promise<string | null> {
  const data = response.data as { code?: string } | null | undefined;
  if (data && typeof data === "object" && typeof data.code === "string") return data.code;
  const err = response.error as { context?: { json?: () => Promise<unknown> } } | null | undefined;
  if (err?.context && typeof err.context.json === "function") {
    try {
      const body = (await err.context.json()) as { code?: string } | null;
      if (body && typeof body.code === "string") return body.code;
    } catch {
      /* cuerpo no JSON */
    }
  }
  return null;
}

/** El mensaje claro para cada código de permiso (clave i18n). */
export function visitErrorKey(code: string | null | undefined, rawMessage?: string | null): string | null {
  const text = `${code ?? ""} ${rawMessage ?? ""}`;
  if (text.includes("REPORT_AUTHOR_ONLY")) return "perito.seguimiento.errors.reportAuthorOnly";
  if (text.includes("VISIT_COMMENT_COMPANY_ONLY")) return "perito.seguimiento.errors.commentCompanyOnly";
  if (text.includes("NOT_ALLOWED")) return "perito.seguimiento.errors.notAllowed";
  if (text.includes("VISIT_NOT_FOUND")) return "perito.seguimiento.errors.notFound";
  return null;
}
